import { zafClient, getZendeskUrl } from "@service/zendesk";

import { type ServiceOrder, type CreateTicketResponse } from "@interfaces/ServiceOrder";
import { getOrCreateUser } from "./user";

export const createTicket = async (
  body: ServiceOrder,
  description: string,
  mainTicket: number
): Promise<CreateTicketResponse> => {
  let userId: number | null = null;
  const IsCpf = body.consumidor.cpf.replace(/\D/g, "").length === 11;
  const IsCnpj = body.consumidor.cpf.replace(/\D/g, "").length === 14;

  const user = await getOrCreateUser(body.consumidor.nome, body.consumidor.email, {
    cpf_3: IsCpf ? body.consumidor.cpf : undefined,
    cnpj_regex: IsCnpj ? body.consumidor.cpf : undefined,
    endereco_2: `${body.consumidor.endereco}, ${body.consumidor.numero}`,
    cidade: body.consumidor.cidade,
    _pessoa_fisica__bairro_de_cobranca: body.consumidor.bairro,
    _pessoa_fisica__bairro_de_entrega: body.consumidor.bairro,
    _pessoa_fisica__endereco_de_entrega: body.consumidor.endereco,
    estado: body.consumidor.estado,
    tipo_de_conta: body.consumidor.tipo_pessoa === "F" ? "pessoa_fisica" : "pessoa_juridica",
    cep__endereco: body.consumidor.cep,
    outro_telefone: body.consumidor.telefone,
  });
  userId = user.id;

  const response = await zafClient.request({
    url: getZendeskUrl() + "/api/v2/tickets.json",
    type: "POST",
    contentType: "application/json",
    data: JSON.stringify({
      ticket: {
        subject: `OS - ${mainTicket}`,
        comment: {
          body: description,
          public: false,
        },
        brand_id: 40709538471444, // sempre a marca SAC AT // Brand sandbox: 45750827384340 - Brand produção: 40709538471444
        type: "incident",
        problem_id: mainTicket,
        requester_id: userId,
        priority: "low",
        ticket_form_id: 38306938336788,
        custom_fields: [
          { id: 38307505268756, value: body.os.sua_os },
          { id: 38309057890068, value: body.os.sua_os },
          {
            id: 38336797292180,
            value:
              body.os.status === "Finalizada" || body.os.finalizada
                ? "fechado_status_ordem_servico"
                : "",
          },
          { id: 38308046409620, value: Number(body.os.fechado_em.match(/\d+/)?.[0]) },
          { id: 38307814846228, value: body.os.data_digitacao.split("/").reverse().join("-") },
          { id: 38307954238228, value: body.os.data_nota_fiscal },
          { id: 38309151507348, value: true },
          {
            id: 38336867152148,
            value:
              body.os.tipo_atendimento.charAt(0).toLowerCase() + body.os.tipo_atendimento.slice(1),
          },
          {
            id: 38307834130964,
            value: body.os.data_entrada_produto_posto?.split("/").reverse().join("-"),
          },
          { id: 38307954238228, value: body.os.finalizada },
          { id: 38307976240660, value: body.os.data_conserto },
          {
            id: 38307771084564,
            value: body.os.data_retirada_produto_consumidor?.split("/").reverse().join("-"),
          },
          { id: 38308660002068, value: body.os.status === "Finalizada" ? true : false },
          { id: 38309074345492, value: body.produto.serie },
          { id: 38307242016788, value: body.produto.aparencia },
          { id: 38307193537428, value: body.produto.acessorio },
          { id: 38308691760916, value: body.produto.informacoes_sobre_defeito },
          { id: 38308017667604, value: body.defeito.reclamado },
          { id: 38308011012628, value: body.defeito.constatado },
          { id: 38336702466324, value: body.defeito.solucao },
          { id: 38308013840660, value: body.os.finalizada },
          { id: 38336582517652, value: body.troca_ressarcimento.responsavel },
          {
            id: 38308665645972,
            value: body.troca_ressarcimento.gera_pedido === "não" ? false : true,
          },
          { id: 38336916676500, value: body.troca_ressarcimento.transporte },
          {
            id: 38336695312660,
            value: (body.troca_ressarcimento.situacao_atendimento ?? "").includes("Garantia")
              ? "produto_em_garantia"
              : (body.troca_ressarcimento.situacao_atendimento ?? "").includes("50%")
                ? "faturado_50"
                : (body.troca_ressarcimento.situacao_atendimento ?? "").includes("100%")
                  ? "faturado_100"
                  : "desconhecido",
          },
          { id: 38308851718036, value: body.troca_ressarcimento.motivo },
          { id: 38307426445844, value: body.troca_ressarcimento.causa_troca },
          { id: 38309125098772, value: body.troca_ressarcimento.observacao },
          { id: 38308971843220, value: body.revenda.nota_fiscal },
          { id: 38336801044884, value: body.consumidor.telefone },
          { id: 38307422509332, value: body.consumidor.celular },
          { id: 38308138125972, value: body.consumidor.email },
          { id: 38308170443540, value: body.consumidor.endereco },
          { id: 38309114260884, value: body.consumidor.numero },
          { id: 38307230752404, value: body.consumidor.bairro },
          { id: 38307424050708, value: body.consumidor.cep },
          { id: 38307431748756, value: body.consumidor.cidade },
          { id: 37809508847764, value: body.consumidor.estado },
          { id: 38307462435732, value: body.revenda.cnpj },
          { id: 38307473553940, value: body.posto.codigo_posto },
          { id: 38307537082900, value: body.consumidor.cpf },
          { id: 38336942532116, value: body.consumidor.estado },
          {
            id: 38307741061396,
            value: body.troca_ressarcimento.data?.split("/").reverse().join("-"),
          },
          {
            id: 38307737856020,
            value: body.revenda.data_nota_fiscal?.split("/").reverse().join("-"),
          },
          // { id: 38308021266068, value: body.produto.descricao },
          { id: 38308638268564, value: body.os.fechado_em },
          { id: 38308680074260, value: body.os.finalizada?.split("/").reverse().join("-") },
          { id: 38308977830420, value: body.consumidor.nome },
          { id: 38309012480916, value: body.revenda.nome },
          { id: 38309138323988, value: body.obs },
          { id: 38309190750228, value: body.os.sua_os },
          { id: 38309281757076, value: body.peca ? "sim_pedido_peca" : "não_pedido_peca" },
          { id: 38336634534292, value: body.troca_ressarcimento.situacao_atendimento },
          { id: 39036904381716, value: body.orientacao_sac },
          { id: 38336919388052, value: body.troca_ressarcimento.trocado_por },
          {
            id: 38336644635796,
            value:
              body.troca_ressarcimento.setor === "Revenda"
                ? "revenda_setor"
                : body.troca_ressarcimento.setor === "Carteira"
                  ? "carteira"
                  : body.troca_ressarcimento.setor === "SAC"
                    ? "sac_setor"
                    : body.troca_ressarcimento.setor === "Procon"
                      ? "procon_setor"
                      : body.troca_ressarcimento.setor === "SAP"
                        ? "sap"
                        : body.troca_ressarcimento.setor === "Suporte Técnico"
                          ? "suporte_técnico"
                          : null,
          },
        ],
      },
    }),
  });

  return response as CreateTicketResponse;
};
